import { useCallback, useEffect, useState } from 'react';
import { useNotify } from 'react-admin';
import {
    Alert,
    AlertTitle,
    Box,
    Button,
    Chip,
    Stack,
    Typography,
} from '@mui/material';
import { fetchRejections, acknowledgeRejection } from './nodeClaimsApi';
import type { NodeClaimRejection } from './types';

const when = (iso: string) => new Date(iso).toLocaleString();

/**
 * Re-claim alerts (#1248): nodes in the field that are being refused.
 *
 * The operator cannot see this any other way. A rejected claim used to be an ILogger warning,
 * so a node sitting unprovisioned and retrying forever was invisible unless someone happened to
 * be reading Seq. It is shown above the list rather than behind a tab because a node that
 * cannot claim is the thing worth interrupting for.
 *
 * Severity follows the operator's next action, not the count. A serial that was never armed is
 * ordinary work — arm it. A node whose live key belongs to different hardware needs that key
 * revoked first, and arming alone will not help, which is the case worth showing as an error.
 */
export const RejectionAlerts = () => {
    const [rejections, setRejections] = useState<NodeClaimRejection[]>([]);
    const [busyId, setBusyId] = useState<number | null>(null);
    const notify = useNotify();

    const load = useCallback(() => {
        fetchRejections(true)
            .then(setRejections)
            .catch(() => {
                // Silent: the alert panel failing must not bury the list underneath it.
                setRejections([]);
            });
    }, []);

    useEffect(load, [load]);

    const acknowledge = async (rejection: NodeClaimRejection) => {
        setBusyId(rejection.id);
        try {
            await acknowledgeRejection(rejection.id);
            notify(`Alert for ${rejection.hardwareSerial} acknowledged`, { type: 'info' });
            setRejections(current => current.filter(r => r.id !== rejection.id));
        } catch (e) {
            notify(e instanceof Error ? e.message : 'Acknowledge failed', { type: 'error' });
        } finally {
            setBusyId(null);
        }
    };

    if (rejections.length === 0) return null;

    return (
        <Box sx={{ mb: 2 }}>
            <Typography variant="overline" color="text.secondary">
                Claim alerts — {rejections.length} node{rejections.length === 1 ? '' : 's'} being refused
            </Typography>

            <Stack spacing={1} sx={{ mt: 1 }}>
                {rejections.map(rejection => (
                    <Alert
                        key={rejection.id}
                        severity={rejection.reasonCode === 'NodeClaim.HeldByOtherHardware' ? 'error' : 'warning'}
                        action={
                            <Button
                                size="small"
                                disabled={busyId === rejection.id}
                                onClick={() => acknowledge(rejection)}
                            >
                                Acknowledge
                            </Button>
                        }
                    >
                        <AlertTitle sx={{ fontFamily: 'monospace' }}>
                            {rejection.hardwareSerial}
                            {rejection.nodeId ? ` → ${rejection.nodeId}` : ' (no matching authorization)'}
                        </AlertTitle>

                        {rejection.reason}

                        <Box sx={{ mt: 1, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                            {/* The count is the signal that this is ongoing rather than a one-off. */}
                            <Chip size="small" label={`${rejection.attemptCount} attempts`} />
                            <Typography variant="caption" color="text.secondary">
                                last {when(rejection.lastSeenAt)}
                                {rejection.lastSeenFromIp ? ` from ${rejection.lastSeenFromIp}` : ''}
                                {' · first '}{when(rejection.firstSeenAt)}
                            </Typography>
                        </Box>
                    </Alert>
                ))}
            </Stack>

            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Acknowledging only clears the alert from this view. It does not let the node claim, and the
                alert returns if the node is refused again.
            </Typography>
        </Box>
    );
};
